import React, { useEffect, useMemo, useState } from 'react';
import '../styles/MiniApp.css';

function MiniApp() {
  const officeAddress = 'с. Косулино ул. Российская д.5';
  const officePhoneDisplay = '+7 343 385-19-00';
  const officePhoneDial = '+73433851900';

  const [status, setStatus] = useState('Подключение к MAX...');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [underDevelopment, setUnderDevelopment] = useState(false);
  const [contractNumber, setContractNumber] = useState('');
  const [password, setPassword] = useState('');
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState('');
  const [billingSuccess, setBillingSuccess] = useState('');
  const [showAuthForm, setShowAuthForm] = useState(false);
  const [showSoon, setShowSoon] = useState(false);
  const [tariffText, setTariffText] = useState('');
  const [planCommentText, setPlanCommentText] = useState('');
  const [ipText, setIpText] = useState('');
  const [balanceText, setBalanceText] = useState('');
  const [enoughToDateText, setEnoughToDateText] = useState('');
  const [statusText, setStatusText] = useState('');
  const [statusKind, setStatusKind] = useState('');
  const [recommendedPayText, setRecommendedPayText] = useState('');
  const [minimalPaySum, setMinimalPaySum] = useState('');
  const [paymentsDisabled, setPaymentsDisabled] = useState(false);
  const [promisePayLoading, setPromisePayLoading] = useState(false);
  const [promisePayModalOpen, setPromisePayModalOpen] = useState(false);
  const [promisePayModalMessage, setPromisePayModalMessage] = useState('');
  const [refreshingMainData, setRefreshingMainData] = useState(false);
  const [topUpModalOpen, setTopUpModalOpen] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState('');
  const [topUpLoading, setTopUpLoading] = useState(false);
  const [topUpError, setTopUpError] = useState('');
  const [suspendedModalOpen, setSuspendedModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('home');

  const bridge = useMemo(() => {
    if (typeof window === 'undefined') return null;
    return window.WebApp || window.Telegram?.WebApp || null;
  }, []);
  const greetingText = useMemo(() => {
    const hour = new Date().getHours();
    return hour >= 18 || hour < 6 ? 'Добрый вечер' : 'Добрый день';
  }, []);

  const openExternalUrl = (url) => {
    if (!url) return;
    try {
      if (bridge?.openLink) {
        bridge.openLink(url);
        return;
      }
    } catch {
      // fallback below
    }

    try {
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      window.location.href = url;
    }
  };

  useEffect(() => {
    const run = async () => {
      try {
        if (!bridge) {
          setStatus('Bridge не найден');
          setError('MAX WebApp bridge не инициализирован. Откройте страницу через кнопку миниприложения в MAX.');
          setLoading(false);
          return;
        }

        bridge.ready?.();
        bridge.expand?.();

        const initData = bridge.initData || '';
        if (!initData) {
          setStatus('Нет initData');
          setError('Не получены данные запуска mini app от MAX.');
          setLoading(false);
          return;
        }

        setStatus('Авторизация...');
        const resp = await fetch('/api/miniapp/auth', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ initData })
        });

        const data = await resp.json().catch(() => ({}));
        if (!resp.ok || !data?.success) {
          if (resp.status === 403 && (data?.code === 'under_development' || String(data?.message || '').includes('скоро все заработает'))) {
            setUnderDevelopment(true);
            setStatus('');
            setError('');
            return;
          }
          throw new Error(data?.message || 'Mini app auth failed');
        }

        if (data.token) {
          localStorage.setItem('user_token', data.token);
        }
        if (data.user) {
          localStorage.setItem('user', JSON.stringify(data.user));
          setUser(data.user);
        }
        setPaymentsDisabled(Boolean(data?.paymentsDisabled));

        const token = data.token || localStorage.getItem('user_token') || '';
        const hasBillingCredentials = Boolean(data.hasBillingCredentials);

        if (hasBillingCredentials) {
          setStatus('Проверка договора...');
          setBillingLoading(true);
          setBillingError('');
          setShowAuthForm(false);

          const autoResp = await fetch('/api/miniapp/billing-auth-auto', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {})
            },
            credentials: 'include'
          });

          const autoData = await autoResp.json().catch(() => ({}));
          if (autoResp.ok && autoData?.success) {
            applyBillingSnapshot(autoData, { showSuspendedModal: true });
          } else {
            setShowAuthForm(true);
            setShowSoon(false);
            setTariffText('');
            setPlanCommentText('');
            setIpText('');
            setBalanceText('');
            setEnoughToDateText('');
            setStatusText('');
            setStatusKind('');
            setMinimalPaySum('');
            setRecommendedPayText('');
            setBillingError(autoData?.message || 'Ошибка авторизации. Введите логин и пароль.');
            setStatus('Требуется авторизация');
          }
        } else {
          setShowAuthForm(true);
          setShowSoon(false);
          setTariffText('');
          setPlanCommentText('');
          setIpText('');
          setBalanceText('');
          setEnoughToDateText('');
          setStatusText('');
          setStatusKind('');
          setMinimalPaySum('');
          setRecommendedPayText('');
          setStatus('Требуется авторизация');
        }
      } catch (err) {
        setError(err.message || 'Ошибка инициализации mini app');
      } finally {
        setBillingLoading(false);
        setLoading(false);
      }
    };

    run();
  }, [bridge]);

  const handleBillingAuth = async (e) => {
    e.preventDefault();

    const contract = contractNumber.trim();
    if (!contract || !password) {
      setBillingSuccess('');
      setBillingError('Введите номер договора и пароль');
      return;
    }

    setBillingLoading(true);
    setBillingError('');
    setBillingSuccess('');

    try {
      const token = localStorage.getItem('user_token') || '';
      const resp = await fetch('/api/miniapp/billing-auth', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        credentials: 'include',
        body: JSON.stringify({ contractNumber: contract, password })
      });

      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data?.success) {
        throw new Error(data?.message || 'Ошибка авторизации');
      }

      setBillingSuccess('');
      applyBillingSnapshot(data, { showSuspendedModal: true });
      setUser((prev) => {
        const next = { ...(prev || {}), miniAppLogin: contract };
        localStorage.setItem('user', JSON.stringify(next));
        return next;
      });
      setStatus('');
    } catch (err) {
      setBillingError(err.message || 'Ошибка авторизации');
    } finally {
      setBillingLoading(false);
    }
  };

  const handleLogoutBilling = async () => {
    try {
      const token = localStorage.getItem('user_token') || '';
      const resp = await fetch('/api/miniapp/logout-billing', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        credentials: 'include'
      });

      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data?.success) {
        throw new Error(data?.message || 'Не удалось выйти из учетной записи');
      }

      setUser((prev) => {
        const next = { ...(prev || {}), miniAppLogin: null };
        localStorage.setItem('user', JSON.stringify(next));
        return next;
      });
      setContractNumber('');
      setPassword('');
      setShowSoon(false);
      setShowAuthForm(true);
      setTariffText('');
      setPlanCommentText('');
      setIpText('');
      setBalanceText('');
      setEnoughToDateText('');
      setStatusText('');
      setStatusKind('');
      setMinimalPaySum('');
      setRecommendedPayText('');
      setBillingError('');
      setBillingSuccess('');
      setStatus('Требуется авторизация');
      setActiveTab('home');
    } catch (err) {
      setBillingError(err.message || 'Не удалось выйти из учетной записи');
      setActiveTab('home');
    }
  };

  const applyBillingSnapshot = (data, options = {}) => {
    const showSuspendedModal = Boolean(options.showSuspendedModal);
    setShowSoon(true);
    setShowAuthForm(false);
    if (typeof data?.paymentsDisabled !== 'undefined') {
      setPaymentsDisabled(Boolean(data.paymentsDisabled));
    }
    setTariffText(data?.tariffText || '');
    setPlanCommentText(data?.planCommentText || '');
    setIpText(data?.ipText || '');
    setBalanceText(data?.balanceText || '');
    setEnoughToDateText(data?.enoughToDateText || '');
    setStatusText(data?.statusText || '');
    setStatusKind(data?.statusKind || '');
    setMinimalPaySum(data?.minimalPaySum || '');
    setRecommendedPayText(data?.recommendedPayText || '');
    setStatus('');
    if (showSuspendedModal && String(data?.statusKind || '') === 'suspended') {
      setSuspendedModalOpen(true);
    }
  };

  const refreshMainData = async () => {
    if (refreshingMainData) return;
    setRefreshingMainData(true);
    try {
      const token = localStorage.getItem('user_token') || '';
      const resp = await fetch('/api/miniapp/billing-auth-auto', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        credentials: 'include'
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok && data?.success) {
        applyBillingSnapshot(data, { showSuspendedModal: false });
      }
    } catch {
      // Silent refresh errors: keep currently visible snapshot.
    } finally {
      setRefreshingMainData(false);
    }
  };

  const handlePromisePay = async () => {
    if (promisePayLoading) return;
    setPromisePayLoading(true);
    try {
      const token = localStorage.getItem('user_token') || '';
      const resp = await fetch('/api/miniapp/promise-pay', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        credentials: 'include'
      });

      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data?.message || 'Ошибка подключения обещанного платежа');
      }

      const modalText = data?.modalMessage || 'Операция выполнена';
      setPromisePayModalMessage(modalText);
      setPromisePayModalOpen(true);
    } catch (err) {
      setPromisePayModalMessage(err.message || 'Ошибка подключения обещанного платежа');
      setPromisePayModalOpen(true);
    } finally {
      setPromisePayLoading(false);
      await refreshMainData();
    }
  };

  const handleTopUpSubmit = async (e) => {
    e.preventDefault();
    if (topUpLoading) return;

    const normalized = String(topUpAmount).replace(',', '.').trim();
    const amount = Number.parseFloat(normalized);
    if (!Number.isFinite(amount) || amount <= 0) {
      setTopUpError('Введите корректную сумму');
      return;
    }

    setTopUpLoading(true);
    setTopUpError('');
    try {
      const token = localStorage.getItem('user_token') || '';
      const resp = await fetch('/api/miniapp/payments/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        credentials: 'include',
        body: JSON.stringify({ amount: Number(amount.toFixed(2)) })
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data?.success) {
        throw new Error(data?.message || 'Не удалось создать платеж');
      }

      if (data.confirmationUrl) {
        const confirmationUrl = String(data.confirmationUrl);
        setTopUpModalOpen(false);
        setTimeout(() => {
          let targetHost = '';
          try {
            targetHost = new URL(confirmationUrl, window.location.origin).hostname.toLowerCase();
          } catch {
            targetHost = '';
          }

          const requiresExternal =
            targetHost.includes('yoomoney.ru') || targetHost.includes('yookassa.ru');

          if (requiresExternal) {
            openExternalUrl(confirmationUrl);
            return;
          }

          try {
            window.location.href = confirmationUrl;
          } catch {
            openExternalUrl(confirmationUrl);
          }
        }, 0);
        return;
      }

      setTopUpModalOpen(false);
      setPromisePayModalMessage('Платеж создан, но ссылка на оплату не получена');
      setPromisePayModalOpen(true);
    } catch (err) {
      setTopUpError(err.message || 'Не удалось создать платеж');
    } finally {
      setTopUpLoading(false);
    }
  };

  const balanceAmount = (() => {
    const raw = String(balanceText || '');
    const normalized = raw.replace('−', '-');
    const match = normalized.match(/Баланс:\s*([+-]?\d+)/i);
    return match ? match[1] : '';
  })();
  const balanceValue = Number.parseInt(balanceAmount, 10);
  const hasNegativeBalance = Number.isFinite(balanceValue) && balanceValue < 0;
  const canShowPromisePayOnHome = statusKind === 'suspended';

  return (
    <div className="miniapp-page">
      <div className={`miniapp-content ${(promisePayModalOpen || topUpModalOpen || suspendedModalOpen) ? 'is-blurred' : ''}`}>
        <div className="miniapp-card">
          <div className="miniapp-welcome-head">
            <img src="/kat.png" alt="KAT Telecom" className="miniapp-welcome-logo" />
            <div className="miniapp-welcome-title-wrap">
              <h1 className="miniapp-welcome-title">{greetingText}</h1>
              <div className="miniapp-welcome-subtitle">{user?.displayName || user?.username || ''}</div>
            </div>
            <div className="miniapp-welcome-user">
              <div className="miniapp-welcome-user-avatar-wrap">
                {user?.avatarUrl ? (
                  <img src={user.avatarUrl} alt="Аватар" className="miniapp-welcome-user-avatar" />
                ) : (
                  <div className="miniapp-welcome-user-avatar-fallback">
                    {(user?.displayName || user?.username || '?').trim().charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
            </div>
          </div>
          {status && <p className="miniapp-status">{status}</p>}
          {loading && <div className="miniapp-loader" />}
          {!loading && underDevelopment && activeTab !== 'contacts' && (
            <div className="miniapp-under-dev">
              <img src="/under-development.svg" alt="В разработке" className="miniapp-under-dev-image" />
              <div className="miniapp-under-dev-text">скоро все заработает</div>
            </div>
          )}
          {error && <div className="miniapp-error">{error}</div>}
          {!loading && !error && (!underDevelopment || activeTab === 'contacts') && (
            <div className="miniapp-user">
              {activeTab === 'home' && showSoon && (
                <>
                  <div className="miniapp-billing-box">
                    {tariffText && <div className="miniapp-billing-row miniapp-tariff-row">{tariffText}</div>}
                    {planCommentText && <div className="miniapp-billing-row miniapp-plan-comment-row">{planCommentText}</div>}
                    {ipText && <div className="miniapp-billing-row">{ipText}</div>}
                  </div>
                  <div className={`miniapp-billing-box miniapp-balance-box ${statusKind === 'active' ? 'miniapp-balance-box-active' : statusKind === 'suspended' ? 'miniapp-balance-box-suspended' : ''}`}>
                    <div className="miniapp-billing-row">
                      {balanceAmount ? <>Баланс: <strong>{balanceAmount}</strong> руб.</> : 'Баланс: —'}
                    </div>
                    {enoughToDateText && <div className="miniapp-billing-row">{enoughToDateText}</div>}
                    {statusText && <div className={`miniapp-billing-row miniapp-status-row ${statusKind === 'active' ? 'status-active' : statusKind === 'suspended' ? 'status-suspended' : ''}`}>{statusText}</div>}
                    {recommendedPayText && <div className="miniapp-billing-row">{recommendedPayText}</div>}
                    {!paymentsDisabled && (
                      <button
                        type="button"
                        className="miniapp-service-btn"
                        onClick={() => {
                          setTopUpError('');
                          setTopUpModalOpen(true);
                        }}
                      >
                        ПОПОЛНИТЬ СЧЕТ
                      </button>
                    )}
                    {canShowPromisePayOnHome && (
                      <>
                        <div className="miniapp-service-note">
                          Опция рассчитана на пользование интернетом в течение трое суток с момента активации, услугой воспользоваться можно только один раз в месяц.
                        </div>
                        <button type="button" className="miniapp-service-btn" onClick={handlePromisePay} disabled={promisePayLoading}>
                          {promisePayLoading ? 'ОБРАБОТКА...' : 'ОБЕЩАННЫЙ ПЛАТЕЖ'}
                        </button>
                      </>
                    )}
                  </div>
                </>
              )}
              {activeTab === 'home' && !showSoon && showAuthForm && (
                <form className="miniapp-auth-form miniapp-auth-panel" onSubmit={handleBillingAuth}>
                  <div className="miniapp-auth-head">
                    <div className="miniapp-auth-title">АВТОРИЗАЦИЯ</div>
                    <div className="miniapp-auth-subtitle">Введите данные договора для входа</div>
                  </div>
                  <div className="miniapp-auth-field">
                    <label className="miniapp-auth-label" htmlFor="contractNumber">Номер договора</label>
                    <input
                      id="contractNumber"
                      className="miniapp-auth-input"
                      type="text"
                      value={contractNumber}
                      onChange={(e) => setContractNumber(e.target.value)}
                      placeholder="Например: 01230"
                    />
                  </div>
                  <div className="miniapp-auth-field">
                    <label className="miniapp-auth-label" htmlFor="contractPassword">Пароль</label>
                    <input
                      id="contractPassword"
                      className="miniapp-auth-input"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Введите пароль"
                    />
                  </div>
                  <button type="submit" className="miniapp-open-btn miniapp-auth-submit" disabled={billingLoading}>
                    {billingLoading ? 'Проверка...' : 'Авторизоваться'}
                  </button>
                  <div className="miniapp-auth-help">
                    <div className="miniapp-auth-help-text">
                      Если забыли или потеряли пароль:
                    </div>
                    <div className="miniapp-auth-help-line">
                      МАКС Абонентский отдел:
                    </div>
                    <div className="miniapp-auth-help-line">
                      +7-922 293-49-35 (только собщения)
                    </div>
                    <div className="miniapp-auth-help-line">
                      или по телефону <a href="tel:+73433851900">+7 (343) 385-19-00</a>
                    </div>
                  </div>
                  {billingError && <div className="miniapp-error">{billingError}</div>}
                  {billingSuccess && <div className="miniapp-status">{billingSuccess}</div>}
                </form>
              )}
              {activeTab === 'profile' && (
                <div className="miniapp-billing-box">
                  <div className="miniapp-profile-head">
                    <div className="miniapp-profile-avatar-wrap">
                      {user?.avatarUrl ? (
                        <img src={user.avatarUrl} alt="Аватар" className="miniapp-profile-avatar" />
                      ) : (
                        <div className="miniapp-profile-avatar-fallback">
                          {(user?.displayName || user?.username || '?').trim().charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div className="miniapp-profile-meta">
                      <div className="miniapp-billing-row">Пользователь: {user?.displayName || user?.username || 'Неизвестно'}</div>
                      <div className="miniapp-billing-row">Ваш номер договора: {user?.miniAppLogin || '—'}</div>
                    </div>
                  </div>
                  <button type="button" className="miniapp-logout-btn" onClick={handleLogoutBilling}>ВЫЙТИ ИЗ УЧЕТНОЙ ЗАПИСИ</button>
                </div>
              )}
              {activeTab === 'services' && (
                <div className="miniapp-billing-box">
                  <div className="miniapp-service-note">
                     Опция рассчитана на пользование интернетом в течение трое суток с момента активации , услугой воспользоваться можно только один раз в месяц.
                  </div>
                  <button type="button" className="miniapp-service-btn" onClick={handlePromisePay} disabled={promisePayLoading}>
                    {promisePayLoading ? 'ОБРАБОТКА...' : 'ОБЕЩАННЫЙ ПЛАТЕЖ'}
                  </button>
                </div>
              )}
              {activeTab === 'contacts' && (
                <div className="miniapp-billing-box miniapp-contacts-box">
                  <div className="miniapp-contacts-grid">
                    <div className="miniapp-contact-card">
                      <div className="miniapp-contact-title">АДРЕС ОФИСА:</div>
                      <div className="miniapp-contact-text">{officeAddress}</div>
                    </div>
                    <div className="miniapp-contact-card">
                      <div className="miniapp-contact-title">ЧАСЫ РАБОТЫ:</div>
                      <div className="miniapp-contact-text">Понедельник-пятница - с 10:00 до 19:00 (без перерыва)</div>
                    </div>
                    <div className="miniapp-contact-card">
                      <div className="miniapp-contact-title">КОНТАКТНЫЙ ТЕЛЕФОН:</div>
                      <div className="miniapp-contact-phone">{officePhoneDisplay}</div>
                      <div className="miniapp-contact-text">Абонентский отдел: клавиша 1</div>
                      <div className="miniapp-contact-text">Тех-поддержка клиентов: клавиша 3</div>
                    </div>
                  </div>
                  <div className="miniapp-contact-actions">
                    <a className="miniapp-service-btn miniapp-contact-btn" href={`tel:${officePhoneDial}`}>ПОЗВОНИТЬ</a>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {!loading && !error && showSoon && (
        <div className={`miniapp-bottom-bar ${(promisePayModalOpen || topUpModalOpen || suspendedModalOpen) ? 'is-blurred' : ''}`}>
          <button type="button" className={`miniapp-bottom-btn ${activeTab === 'home' ? 'is-active' : ''}`} onClick={() => setActiveTab('home')}>Главная</button>
          <button type="button" className={`miniapp-bottom-btn ${activeTab === 'services' ? 'is-active' : ''}`} onClick={() => setActiveTab('services')}>Услуги</button>
          <button type="button" className={`miniapp-bottom-btn ${activeTab === 'contacts' ? 'is-active' : ''}`} onClick={() => setActiveTab('contacts')}>Контакты</button>
          <button type="button" className={`miniapp-bottom-btn ${activeTab === 'profile' ? 'is-active' : ''}`} onClick={() => setActiveTab('profile')}>Профиль</button>
        </div>
      )}
      {promisePayModalOpen && (
        <div className="miniapp-modal-overlay" role="dialog" aria-modal="true">
          <div className="miniapp-modal">
            <div className="miniapp-modal-message">{promisePayModalMessage}</div>
            <button type="button" className="miniapp-service-btn miniapp-modal-close-btn" onClick={() => setPromisePayModalOpen(false)}>
              Закрыть
            </button>
          </div>
        </div>
      )}
      {topUpModalOpen && (
        <div className="miniapp-modal-overlay" role="dialog" aria-modal="true">
          <form className="miniapp-modal" onSubmit={handleTopUpSubmit}>
            <div className="miniapp-modal-message">Введите сумму для оплаты</div>
            <input
              className="miniapp-auth-input miniapp-modal-input"
              type="text"
              inputMode="decimal"
              value={topUpAmount}
              onChange={(e) => setTopUpAmount(e.target.value)}
              placeholder="Например: 1000"
            />
            {topUpError && <div className="miniapp-error">{topUpError}</div>}
            <button type="submit" className="miniapp-service-btn miniapp-modal-close-btn" disabled={topUpLoading}>
              {topUpLoading ? 'СОЗДАНИЕ ПЛАТЕЖА...' : 'ОПЛАТИТЬ'}
            </button>
            <button
              type="button"
              className="miniapp-service-btn miniapp-modal-close-btn"
              onClick={() => setTopUpModalOpen(false)}
              disabled={topUpLoading}
            >
              ЗАКРЫТЬ
            </button>
          </form>
        </div>
      )}
      {suspendedModalOpen && (
        <div className="miniapp-modal-overlay" role="dialog" aria-modal="true">
          <div className="miniapp-modal">
            <div className="miniapp-modal-title-danger">ДОСТУП ПРИОСТАНОВЛЕН</div>
            <div className="miniapp-modal-message">
              Необходимо пополнить баланс !
              <br />
              на сумму {minimalPaySum || '0'} руб.
              <br />
              или подключите обещанный платеж.
            </div>
            <button type="button" className="miniapp-service-btn miniapp-modal-close-btn" onClick={() => setSuspendedModalOpen(false)}>
              ЗАКРЫТЬ
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default MiniApp;
